<script lang="ts">
  import type { IterationProgress, PreviewFrame } from '$lib/types'

  interface Props {
    frame: PreviewFrame | null
    progress: IterationProgress | null
  }

  const { frame, progress }: Props = $props()

  let canvas = $state<HTMLCanvasElement | null>(null)

  // Frames arrive every few iterations as 8-bit ink coverage; expand to RGBA
  // and blit. Drawing straight from the worker's buffer avoids a second copy.
  $effect(() => {
    const target = canvas
    if (!target || !frame) return
    target.width = frame.width
    target.height = frame.height
    const ctx = target.getContext('2d')
    if (!ctx) return

    const rgba = new Uint8ClampedArray(frame.width * frame.height * 4)
    for (let i = 0; i < frame.pixels.length; i++) {
      const ink = frame.pixels[i]
      const value = 255 - ink
      rgba[i * 4] = value
      rgba[i * 4 + 1] = value
      rgba[i * 4 + 2] = value
      rgba[i * 4 + 3] = 255
    }
    ctx.putImageData(new ImageData(rgba, frame.width, frame.height), 0, 0)
  })

  const percent = $derived(progress ? Math.round((progress.iteration / progress.total) * 100) : 0)
</script>

<figure class="live" data-testid="live-preview">
  {#if frame}
    <canvas bind:this={canvas} data-testid="live-canvas"></canvas>
  {:else}
    <div class="placeholder">Preparing the first frame…</div>
  {/if}

  {#if progress}
    <figcaption>
      <span data-testid="live-iteration">
        Optimizing {progress.iteration}/{progress.total}
      </span>
      <span class="loss" data-testid="live-loss">loss {progress.loss.toExponential(2)}</span>
      <span class="loss">{progress.strokeCount} strokes</span>
    </figcaption>
    <div
      class="bar"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin="0"
      aria-valuemax="100"
      aria-label="Optimization progress"
    >
      <div class="fill" style="width: {percent}%"></div>
    </div>
  {/if}
</figure>

<style>
  .live {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  canvas,
  .placeholder {
    display: block;
    width: 100%;
    height: auto;
    max-height: 70vh;
    object-fit: contain;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }

  .placeholder {
    display: grid;
    place-items: center;
    min-height: 12rem;
    color: var(--text-muted);
    background: var(--surface);
  }

  figcaption {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.8rem;
    color: var(--text-muted);
  }

  .loss {
    font-family: var(--mono);
  }

  .bar {
    height: 4px;
    background: var(--surface-raised);
    border-radius: 999px;
    overflow: hidden;
  }

  .fill {
    height: 100%;
    background: var(--accent);
    transition: width 120ms linear;
  }
</style>
